import { Box, combineSx, Portal, SxProps } from '@chronosphereio/chrono-ui';
import { memo, ReactNode, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { CursorCoordinates, PANEL_TOOLTIP_MAX_HEIGHT, PanelTooltipConfig } from './model';
import { PanelTooltipHeader } from './PanelTooltipHeader';
import { usePanelTooltipActions, usePanelTooltipState } from './PanelTooltipProvider';
import {
  PanelTooltipOverlay,
  TooltipDynamicContentWrapper,
  TooltipLayout,
  TooltipStaticContentWrapper,
} from './TooltipLayout';
import { usePanelTooltipPortalContainer } from './usePanelTooltipPortalContainer';
import { assembleTransform } from './utils';

type TooltipRenderParams = {
  mousePos: CursorCoordinates | null;
  pinnedCoords: CursorCoordinates | undefined;
  activePanelKey: string | undefined;
  isPinned: boolean;
  isOverChart: boolean;
};

interface PanelTooltipWrapperProps {
  children: ReactNode;
  width: number;
  tooltipConfig: PanelTooltipConfig;
  panelKey: string;
  sx?: SxProps;
  onUnpinClick?: () => void;
  /**
   * Renderer to compute tooltip content based on dynamic render state
   */
  renderTooltip?: (params: TooltipRenderParams) => ReactNode | null;
  headerTitle?: string;
  /**
   * Parent panel key to restore when mouse leaves this wrapper
   * Prevents tooltip state conflicts when wrappers are nested
   */
  parentPanelKey?: string;
}

/**
 * Wrapper component that provides tooltip functionality. Ensures pin/unpin works on click
 * and mouse handlers are set up correctly so tooltip is shown above the active panel.
 * When a tooltip is pinned, the wrapper automatically caches the last rendered content
 * so it persists even when the mouse moves away.
 */
export function PanelTooltipWrapper({
  width,
  children,
  tooltipConfig,
  panelKey,
  sx,
  onUnpinClick,
  renderTooltip,
  headerTitle,
  parentPanelKey,
}: PanelTooltipWrapperProps) {
  const [tooltipRootEl, setTooltipRootEl] = useState<HTMLDivElement | null>(null);
  // Get container element to attach tooltip portal to.
  const { tooltipPortalContainerEl: containerElement } = usePanelTooltipPortalContainer({
    rootEl: tooltipRootEl,
  });

  const { isPanelPinned, handlePin, handleUnpin, setActivePanelKey } = usePanelTooltipActions();
  const { mousePos, pinnedCoords, activePanelKey } = usePanelTooltipState();

  // Snapshot of tooltip content captured at pin time so pinned tooltips survive mouse-leave.
  // Cleared whenever the mouse leaves or content becomes null to avoid showing stale data.
  const pinnedContentRef = useRef<ReactNode | null>(null);

  const renderTooltipContent = () => {
    if (!tooltipConfig.enabled || !renderTooltip) return null;

    const isPinned = isPanelPinned(panelKey);
    const currentPinnedCoords = isPinned ? pinnedCoords : undefined;

    // Determine display content: use cached snapshot when pinned, otherwise compute live
    let displayContent: ReactNode | null = null;

    if (isPinned && pinnedContentRef.current) {
      // When pinned, show the cached content regardless of current mouse state
      displayContent = pinnedContentRef.current;
    } else if (mousePos) {
      const content = renderTooltip({
        mousePos,
        pinnedCoords: currentPinnedCoords,
        activePanelKey,
        isPinned,
        isOverChart: activePanelKey === panelKey,
      });

      // Cache the latest content so it can be preserved when the user pins.
      // Clearing on null prevents stale content from a previous hover session
      // being shown if the user pins after the mouse has already left.
      pinnedContentRef.current = content;

      if (content && activePanelKey === panelKey) {
        displayContent = content;
      }
    } else {
      // No mousePos and not pinned — clear stale cache
      pinnedContentRef.current = null;
    }

    if (!displayContent) return null;

    return (
      <PanelTooltip
        containerElement={containerElement}
        headerTitle={headerTitle}
        mousePos={mousePos}
        pinnedCoords={currentPinnedCoords}
        isPinned={isPinned}
        enablePinning={tooltipConfig.enablePinning}
        onUnpinClick={() => {
          pinnedContentRef.current = null;
          onUnpinClick?.();
          handleUnpin();
        }}
      >
        {displayContent}
      </PanelTooltip>
    );
  };

  return (
    <Box
      ref={setTooltipRootEl}
      sx={combineSx({
        width,
        overflow: 'auto',
        position: 'relative',
        ...sx,
      })}
      onMouseEnter={() => {
        setActivePanelKey(panelKey);
      }}
      onMouseLeave={() => {
        // Restore parent panel key if nested, otherwise clear to prevent tooltips showing elsewhere
        setActivePanelKey(parentPanelKey);
      }}
      onClick={(e) => {
        // Handle clicks on the entire panel area for pinning/unpinning
        if (!tooltipConfig.enabled || !tooltipConfig.enablePinning) {
          return;
        }
        e.stopPropagation(); // Important to stop immediate unpin from PanelTooltipProvider handleGlobalClick
        if (isPanelPinned(panelKey)) {
          pinnedContentRef.current = null;
          handleUnpin();
        } else if (mousePos) {
          handlePin(panelKey, mousePos);
        }
      }}
    >
      {renderTooltipContent()}
      {children}
    </Box>
  );
}

export interface PanelTooltipProps {
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  containerElement?: HTMLElement | null;
  mousePos?: CursorCoordinates;
  pinnedCoords?: CursorCoordinates;
  isPinned: boolean;
  headerTitle?: string;
  enablePinning?: boolean;
  maxTooltipHeight?: number;
  onUnpinClick?: () => void;
}

export const PanelTooltip = memo(function PanelTooltip({
  children,
  header,
  footer,
  containerElement,
  mousePos,
  headerTitle,
  pinnedCoords,
  isPinned,
  enablePinning = true,
  maxTooltipHeight = PANEL_TOOLTIP_MAX_HEIGHT,
  onUnpinClick,
}: PanelTooltipProps) {
  const transform = useRef<string>();
  const { width: tooltipWidth, height: tooltipHeight, ref: tooltipRef } = useResizeObserver();

  if (!isPinned) {
    transform.current = assembleTransform({
      mousePos,
      pinnedPos: pinnedCoords,
      tooltipHeight: tooltipHeight ?? 0,
      tooltipWidth: tooltipWidth ?? 0,
      containerElement,
    });
  }

  const finalHeader = (() => {
    if (header) {
      return header; // Optional custom header
    }
    return (
      <PanelTooltipHeader
        title={headerTitle}
        isPinned={isPinned}
        enablePinning={enablePinning}
        onUnpinClick={onUnpinClick}
      />
    );
  })();

  return (
    <Portal container={containerElement}>
      <PanelTooltipOverlay
        role="tooltip"
        ref={tooltipRef}
        style={{
          maxHeight: maxTooltipHeight,

          transform: transform.current,
        }}
        // Prevent unpinning when clicking inside the pinned tooltip content
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <TooltipLayout>
          {finalHeader && <TooltipStaticContentWrapper>{finalHeader}</TooltipStaticContentWrapper>}
          <TooltipDynamicContentWrapper>{children}</TooltipDynamicContentWrapper>
          {footer && <TooltipStaticContentWrapper>{footer}</TooltipStaticContentWrapper>}
        </TooltipLayout>
      </PanelTooltipOverlay>
    </Portal>
  );
});
